import { apiUrl } from '@dashboard/services/env';
import storage from '@dashboard/services/storage';

class GoogleAuthService {
  /**
   * Generate Google OAuth authorization URL
   * @param {string} redirectURI - The redirect URI for OAuth callback
   * @param {string} state - Optional state parameter for CSRF protection
   * @returns {Promise<string>} The authorization URL
   */
  static async getAuthUrl(redirectURI, state = null) {
    const response = await fetch(`${apiUrl}/auth/google/url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        redirectURI,
        ...(state && { state }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to generate auth URL');
    }

    const data = await response.json();
    return data.data.authUrl;
  }

  /**
   * Handle Google OAuth login
   * @param {string} code - Authorization code from Google
   * @param {string} redirectURI - The redirect URI used in the authorization request
   * @param {boolean} marketingConsent - User's marketing consent
   * @param {string} invite - Optional invitation token
   * @param {string} gclid - Optional Google Click ID for attribution
   * @param {string} sessionId - Optional session ID for tracking
   * @returns {Promise<Object>} Login response
   */
  static async login(code, redirectURI, marketingConsent = false, invite = null, gclid = null, sessionId = null) {
    const response = await fetch(`${apiUrl}/auth/google/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirectURI,
        marketingConsent,
        ...(invite && { inviteToken: invite }),
        ...(gclid && { gclid }),
        ...(sessionId && { sessionId }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Google login failed');
    }

    return await response.json();
  }

  /**
   * Link Google account to existing user
   * @param {Object} googleProfile - Google profile data from OAuth
   * @param {string} email - User's email address
   * @param {string} password - User's password for verification
   * @param {string} invite - Optional invitation token
   * @returns {Promise<Object>} Link response
   */
  static async linkAccount(googleProfile, email, password, invite = null) {
    const response = await fetch(`${apiUrl}/auth/google/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        googleProfile,
        email,
        password,
        ...(invite && { inviteToken: invite }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Account linking failed');
    }

    return await response.json();
  }

  /**
   * Accept team invitation via Google OAuth
   * @param {string} code - Authorization code from Google
   * @param {string} redirectURI - The redirect URI used in the authorization request
   * @param {string} invite - Invitation token
   * @param {boolean} marketingConsent - User's marketing consent
   * @param {string} gclid - Optional Google Click ID for attribution
   * @param {string} sessionId - Optional session ID for tracking
   * @returns {Promise<Object>} Invite acceptance response
   */
  static async acceptInvite(code, redirectURI, invite, marketingConsent = false, gclid = null, sessionId = null) {
    const response = await fetch(`${apiUrl}/auth/google/accept-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirectURI,
        inviteToken: invite,
        marketingConsent,
        ...(gclid && { gclid }),
        ...(sessionId && { sessionId }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invite acceptance failed');
    }

    return await response.json();
  }

  /**
   * Unlink Google account from user
   * @returns {Promise<Object>} Unlink response
   */
  static async unlinkAccount() {
    const token = storage.local.getItem('auth_token');
    const response = await fetch(`${apiUrl}/auth/google/unlink`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': token,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to unlink Google account');
    }

    return await response.json();
  }

  /**
   * Start Google OAuth flow by redirecting to Google
   * @param {Object} options - OAuth options
   * @param {string} options.redirectURI - The redirect URI for OAuth callback
   * @param {string} options.state - Optional state parameter
   */
  static async startOAuthFlow({ redirectURI, state }) {
    try {
      const authUrl = await this.getAuthUrl(redirectURI, state);

      // Redirect current window to Google OAuth
      window.location.href = authUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generate redirect URI for current environment
   * @param {string} path - The callback path (default: '/auth/google/callback')
   * @returns {string} Full redirect URI
   */
  static getRedirectURI(path = '/auth/google/callback') {
    return `${window.location.origin}${path}`;
  }

  /**
   * Parse invite token from URL parameters
   * @returns {string|null} Invite token if present
   */
  static getInviteTokenFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('invite') || urlParams.get('inviteToken');
  }

  /**
   * Generate state parameter with invite token and redirect info
   * @param {string} redirectPath - Path to redirect after auth
   * @param {string} invite - Optional invite token
   * @returns {string} Base64 encoded state
   */
  static generateState({
    redirectPath = '/dashboard',
    invite = null,
    team = null,
    returnPath = null,
  }) {
    const state = {
      redirectPath,
      ...(invite && { invite }),
      ...(team && { team }),
      ...(returnPath && { returnPath }),
      timestamp: Date.now(),
    };

    return btoa(JSON.stringify(state));
  }

  /**
   * Parse state parameter
   * @param {string} state - Base64 encoded state
   * @returns {Object|null} Parsed state object
   */
  static parseState(state) {
    try {
      return JSON.parse(atob(state));
    } catch (error) {
      console.error('Failed to parse OAuth state:', error);
      return null;
    }
  }
}

export default GoogleAuthService;
